import { isPendingApproval } from './vendorEntity';

export const VENDOR_STATUS = {
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  SUSPENDED: 'SUSPENDED',
  BLACKLISTED: 'BLACKLISTED',
};

export const WORKFLOW_STAGES = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { key: 'APPROVED', label: 'Approved' },
];

export const ACTION_CONFIG = {
  submit: {
    key: 'submit',
    label: 'Submit for Approval',
    title: 'Submit for Approval',
    description: 'Submit this vendor for approval? Reviewers will verify compliance documents before approval.',
    tone: 'mint',
    primary: true,
  },
  approve: {
    key: 'approve',
    label: 'Approve',
    title: 'Approve Vendor',
    description: 'Approve this vendor? They will be eligible for new purchase orders.',
    primary: true,
  },
  reject: {
    key: 'reject',
    label: 'Reject',
    title: 'Reject Vendor',
    description: 'Reject this vendor? Provide a reason so the creator can address issues.',
    tone: 'red',
    variant: 'outlined',
    destructive: true,
    requireReason: true,
  },
  suspend: {
    key: 'suspend',
    label: 'Suspend',
    title: 'Suspend Vendor',
    description: 'Temporarily suspend this vendor? They cannot be used in new purchase orders until reactivated.',
    tone: 'amber',
    variant: 'outlined',
    requireReason: true,
  },
  blacklist: {
    key: 'blacklist',
    label: 'Blacklist',
    title: 'Blacklist Vendor',
    description: 'Permanently blacklist this vendor? This action cannot be undone through the UI.',
    tone: 'red',
    variant: 'outlined',
    destructive: true,
    requireReason: true,
  },
  reactivate: {
    key: 'reactivate',
    label: 'Reactivate',
    title: 'Reactivate Vendor',
    description: 'Reactivate this vendor? They will return to approved status and can be used in purchase orders.',
    tone: 'mint',
    primary: true,
  },
};

export const ACTION_SUCCESS_MESSAGES = {
  submit: 'Vendor submitted for approval.',
  approve: 'Vendor approved successfully.',
  reject: 'Vendor rejected.',
  suspend: 'Vendor suspended.',
  blacklist: 'Vendor blacklisted.',
  reactivate: 'Vendor reactivated.',
};

export function isVendorActive(v) {
  return v?.is_active !== false;
}

export function isPurchasableVendor(v) {
  return isVendorActive(v)
    && v?.approval_status === VENDOR_STATUS.APPROVED
    && v?.can_purchase !== false;
}

export function getWorkflowStageIndex(status) {
  if (status === 'SUBMITTED') return 1;
  const idx = WORKFLOW_STAGES.findIndex((s) => s.key === status);
  if (idx >= 0) return idx;
  if (status === VENDOR_STATUS.REJECTED) return 0;
  if (status === VENDOR_STATUS.SUSPENDED || status === VENDOR_STATUS.BLACKLISTED) return 2;
  return -1;
}

export function getWorkflowBanner(vendor) {
  const status = vendor?.approval_status;
  const active = isVendorActive(vendor);

  if (!active) {
    return {
      severity: 'warning',
      text: 'This vendor is inactive and cannot be submitted for approval or used in new purchase orders.',
    };
  }
  if (status === VENDOR_STATUS.DRAFT) {
    return {
      severity: 'info',
      text: 'Vendor saved as Draft. Review supporting documents, then submit for approval.',
    };
  }
  if (isPendingApproval(status)) {
    return {
      severity: 'info',
      text: 'Awaiting approval. An authorized user must approve before purchase orders can be raised.',
    };
  }
  if (status === VENDOR_STATUS.APPROVED && isPurchasableVendor(vendor)) {
    return {
      severity: 'success',
      text: 'Vendor is approved and available for purchase orders.',
    };
  }
  if (status === VENDOR_STATUS.REJECTED) {
    return {
      severity: 'error',
      text: 'Vendor was rejected. Update details and resubmit for approval.',
    };
  }
  if (status === VENDOR_STATUS.SUSPENDED) {
    return {
      severity: 'warning',
      text: 'Vendor is suspended and blocked from new purchase orders.',
    };
  }
  if (status === VENDOR_STATUS.BLACKLISTED) {
    return {
      severity: 'error',
      text: 'Vendor is blacklisted and permanently blocked from purchase orders.',
    };
  }
  return null;
}

export function getAvailableActions(vendor) {
  const status = vendor?.approval_status;
  const active = isVendorActive(vendor);
  const keys = [];

  if (status === VENDOR_STATUS.DRAFT && active) keys.push('submit');
  if (isPendingApproval(status) && active) keys.push('approve', 'reject');
  if (status === VENDOR_STATUS.APPROVED) keys.push('suspend', 'blacklist');
  if (status === VENDOR_STATUS.SUSPENDED) keys.push('reactivate');

  return keys.map((key) => ACTION_CONFIG[key]).filter(Boolean);
}

/** Form edit mode based on approval status. */
export function getVendorFormMode(approvalStatus) {
  if (!approvalStatus || approvalStatus === VENDOR_STATUS.DRAFT || approvalStatus === VENDOR_STATUS.REJECTED) {
    return 'full';
  }
  if (approvalStatus === VENDOR_STATUS.PENDING_APPROVAL || isPendingApproval(approvalStatus)) {
    return 'readonly';
  }
  if (approvalStatus === VENDOR_STATUS.APPROVED) {
    return 'limited';
  }
  return 'readonly';
}

/** Fields editable when form mode is "limited" (approved vendors). */
export const LIMITED_EDIT_FIELDS = new Set([
  'vendor_name', 'legal_name', 'vendor_category', 'remarks',
  'contact_person', 'phone', 'alternate_phone', 'email',
  'payment_terms', 'credit_period', 'bank_name', 'bank_account', 'bank_ifsc',
  'is_active', 'documentRows',
]);

export function isFieldEditable(fieldName, formMode) {
  if (formMode === 'full') return true;
  if (formMode === 'readonly') return false;
  if (formMode === 'limited') return LIMITED_EDIT_FIELDS.has(fieldName);
  return false;
}

export function canEditVendor(vendor) {
  return getVendorFormMode(vendor?.approval_status) !== 'readonly';
}
